import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import AuthBrandPanel, { AuthBrandMark } from '../components/auth/AuthBrandPanel';
import GoogleSignInButton from '../components/auth/GoogleSignInButton';
import { Button, Input } from '@copilot/ui';
import { Field } from '../components/common/Field';
import { useAuth } from '../hooks/useAuth';
import { getAuthenticatedHomePath } from '../utils/authRedirects';
import { getUserFriendlyError } from '../utils/errors';

const ACCOUNT_DISABLED_CODE = 'ACCOUNT_DISABLED';
// The password was correct but the address was never confirmed. Not an
// error to show - a step to finish, so the user is taken to it.
const EMAIL_NOT_VERIFIED_CODE = 'EMAIL_NOT_VERIFIED';
const ACCOUNT_DISABLED_MESSAGE =
  'Your account has been suspended. Please contact administrator.';

function LoginPage() {
  const { login, loginWithGoogle, sessionExpired, clearSessionExpired } = useAuth();
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(true);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Google's button owns its own click/loading state, so this only has
  // to deal with the outcome. Errors surface in the SAME alert the
  // password form uses - one place to look, whichever way you signed in.
  const handleGoogleCredential = async (credential) => {
    setError('');

    try {
      const me = await loginWithGoogle(credential);
      navigate(getAuthenticatedHomePath(me), { replace: true });
    } catch (err) {
      setError(
        err.code === ACCOUNT_DISABLED_CODE
          ? ACCOUNT_DISABLED_MESSAGE
          : getUserFriendlyError(
              err,
              'Unable to connect. Please try again.',
              'Google Sign-In failed. Please try again.'
            )
      );
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setIsSubmitting(true);

    try {
      const me = await login({ email, password, rememberMe });
      navigate(getAuthenticatedHomePath(me), { replace: true });
    } catch (err) {
      if (err.code === EMAIL_NOT_VERIFIED_CODE) {
        navigate('/verify-email', { state: { email } });
        return;
      }

      setError(
        err.code === ACCOUNT_DISABLED_CODE
          ? ACCOUNT_DISABLED_MESSAGE
          : getUserFriendlyError(
              err,
              'Unable to connect. Please try again.',
              'Invalid email or password.'
            )
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-shell">
        <AuthBrandPanel />

        <div className="auth-card">
          <AuthBrandMark className="mobile-only" />

          <section className="auth-form-panel" aria-labelledby="login-heading">
            <div className="auth-heading" id="login-heading">Welcome Back</div>
            <p className="auth-panel-subtitle">
              Sign in to continue
            </p>

            {sessionExpired && (
              <div
                className="form-alert"
                style={{ marginBottom: 'var(--space-4)' }}
                role="status"
              >
                Your session expired. Please log in again.
              </div>
            )}

            <form
              className="auth-form"
              onSubmit={handleSubmit}
              onFocus={clearSessionExpired}
            >
              <Field label="Email" htmlFor="login-email">
                <Input
                  id="login-email"
                  type="email"
                  autoComplete="email"
                  placeholder="user@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </Field>

              <Field label="Password" htmlFor="login-password">
                <Input revealable type="password"
                  id="login-password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </Field>

              <div className="auth-row-between">
                <label>
                  <input
                    type="checkbox"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                  />{' '}
                  Remember me
                </label>

                <Link to="/forgot-password">Forgot Password</Link>
              </div>

              {error && <div className="form-alert">{error}</div>}

              <Button type="submit" block disabled={isSubmitting}>
                {isSubmitting ? 'Signing in...' : 'Sign In'}
              </Button>
            </form>

            <div className="auth-divider" role="separator">
              <span>or</span>
            </div>

            <GoogleSignInButton
              onCredential={handleGoogleCredential}
              text="signin_with"
            />
          </section>

          <div className="auth-footer-link">
            Don&apos;t have an account? <Link to="/register">Sign Up</Link>
          </div>
        </div>
      </div>
    </div>
  );
}

export default LoginPage;
